import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext/AuthContext'; 
import PostWithdrawalRequestService from './service/postWithdrawalRequest'; 
import { CryptoAccountCreationDTO } from './service/models'; 

const textFieldStyle = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    color: '#FFFFFF',
    borderRadius: 2,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#5B68F6',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#9CA3AF',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#5B68F6',
  },
};

const resizeImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const maxSize = 1024;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height *= maxSize / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width *= maxSize / height;
            height = maxSize;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Compression failed'));
            }
          }, 'image/jpeg', 0.85);
        } else {
          reject(new Error('Canvas context not available'));
        }
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

interface AddCryptoAccountModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCryptoAccountModal: React.FC<AddCryptoAccountModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('area');
  const { tokens } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<Omit<CryptoAccountCreationDTO, 'cryptoAddressFileId'>>({
    accountLabel: '',
    cryptoAddress: '',
    cryptoNetwork: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({ accountLabel: '', cryptoAddress: '', cryptoNetwork: '' });
      setSelectedFile(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.cryptoAddress || !formData.cryptoNetwork || !selectedFile) {
      setError(t('Veuillez remplir tous les champs et uploader le QR Code'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (tokens) {
        const compressedFile = await resizeImage(selectedFile);

        const fileId = await PostWithdrawalRequestService.uploadCryptoProof(
          tokens.accessToken,
          compressedFile
        );

        const finalLabel = formData.accountLabel.trim() || `Wallet ${formData.cryptoNetwork}`;

        const creationDTO: CryptoAccountCreationDTO = {
          ...formData,
          accountLabel: finalLabel, 
          cryptoAddressFileId: fileId,
        };

        await PostWithdrawalRequestService.createUserCryptoAccount(
          tokens.accessToken,
          creationDTO,
        );

        onSuccess();
        onClose();
        
        setFormData({ accountLabel: '', cryptoAddress: '', cryptoNetwork: '' });
        setSelectedFile(null);
      }
    } catch (err: any) {
      console.error(err);
      const msg = err.response?.data?.errors?.request || t('Une erreur est survenue lors de la création');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#0f1116',
          color: '#FFFFFF',
          borderRadius: 2,
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ fontSize: 24, fontWeight: 600, color: '#FFFFFF', pb: 2, pt: 3, px: 3 }}>
        {t('Ajouter une adresse crypto')}
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <X size={20} />
      </IconButton>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <TextField
            label={t('Libellé (Optionnel)')}
            name="accountLabel"
            value={formData.accountLabel}
            onChange={handleChange}
            fullWidth
            placeholder={t('Ex: Wallet Binance')}
            sx={textFieldStyle}
          />
          <TextField
            select
            label={t('Réseau')}
            name="cryptoNetwork"
            value={formData.cryptoNetwork}
            onChange={handleChange}
            required
            fullWidth
            sx={textFieldStyle}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: '#1a1d24',
                    color: '#FFFFFF',
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: 'rgba(91, 104, 246, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(91, 104, 246, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(91, 104, 246, 0.3)',
                        },
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="USDT TRC-20">USDT TRC-20</MenuItem>
            <MenuItem value="USDT ERC-20">USDT ERC-20</MenuItem>
            <MenuItem value="USDC ERC-20">USDC ERC-20</MenuItem>
          </TextField>
          <TextField
            label={t('Adresse du portefeuille')}
            name="cryptoAddress"
            value={formData.cryptoAddress}
            onChange={handleChange}
            required
            fullWidth
            sx={textFieldStyle}
          />

          <Box 
            sx={{ 
              border: '2px dashed rgba(255, 255, 255, 0.2)', 
              borderRadius: 2, 
              p: 3, 
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: 'transparent', 
              transition: 'all 0.2s ease',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              }
            }}
            onClick={() => document.getElementById('crypto-file-input')?.click()}
          >
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="crypto-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <Stack direction="column" alignItems="center" spacing={1.5}>
              <CloudUploadIcon sx={{ color: '#5B68F6', fontSize: 48 }} />
              <Typography variant="body2" sx={{ color: selectedFile ? '#FFFFFF' : '#9CA3AF' }}>
                {selectedFile 
                  ? `Fichier : ${selectedFile.name}` 
                  : t('Cliquez pour uploader une capture du QR Code (Requis)')}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            color: '#9CA3AF',
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 500,
            px: 3,
            '&:hover': { 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#FFFFFF',
            }
          }}
        >
          {t('Annuler')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          sx={{
            backgroundColor: '#354eef',
            color: '#FFFFFF',
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 1.5,
            '&:hover': { 
              backgroundColor: '#4C5CE6',
            },
            '&:disabled': {
              backgroundColor: 'rgba(91, 104, 246, 0.4)',
              color: 'rgba(255, 255, 255, 0.5)',
            }
          }}
        >
          {loading ? t('Enregistrement...') : t('Enregistrer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCryptoAccountModal;